/**
 * usePayloadSchema — TanStack Query wrapper around
 * ``GET /api/v1/actions/proposals/{proposal_id}/schema``.
 *
 * Powers the HITL `<ActionFieldEditor>` (`@ando/ui`) with the writable
 * field surface for a given action proposal. The BE endpoint already
 * exists (``src/api/v1/endpoints/actions.py::get_proposal_schema``) and
 * dispatches per `action_type` internally — the FE never speaks
 * `action_type`, only `proposal_id`.
 *
 * Per CLAUDE.md `feedback_hitl_card_editable_fields.md`: every AI-proposed
 * field MUST be editable. The schema returned by the BE includes
 * `readonly: false` for editable fields and `required: true` for mandatory
 * ones; the editor enforces both.
 *
 * Per CLAUDE.md `feedback_fic_schedule_email_recipient_visibility.md`: FiC
 * `schedule_invoice_email` recipient_email is an action-time param the BE
 * does NOT include in `payload_schema(Fattura)`. This hook surfaces the
 * payload schema as-is; the FE composer is responsible for prominently
 * rendering `recipient_email` as a separate, validated field. See
 * `components/ActionFormSurface` (already mounted, A.7).
 */

import { useMemo } from "react";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";

import { apiClient } from "@ando/core/api";
import type { UnifiedFieldSpecWire, UnifiedPayloadSchemaWire } from "@ando/core";
import type { ActionFieldSpec, ActionFieldValue } from "@ando/ui";

// ----- Wire types (mirror BE `FieldSpecResponse` in actions.py) ----------

export interface PayloadFieldSpec {
  readonly name: string;
  readonly data_type: string;
  readonly required?: boolean;
  readonly readonly?: boolean;
  readonly max_length?: number | null;
  readonly enum_values?: readonly string[];
  readonly reference_to?: string | null;
  readonly description?: string;
  readonly default_value?: unknown;
  readonly validation_pattern?: string | null;
}

export interface PayloadSchema {
  readonly proposal_id: string;
  readonly provider: string;
  readonly sobject_type: string;
  readonly for_create: boolean;
  readonly fields: readonly PayloadFieldSpec[];
}

// ----- Adapter: BE FieldSpec → @ando/ui ActionFieldSpec ------------------

/**
 * Map the BE wire shape to the editor's declarative spec.
 *
 * Type coercion table:
 *   string → "string" (or "email"/"iban"/"vat_id" when name hints it)
 *   text   → "text"
 *   int/float/decimal → "number"
 *   currency → "currency"
 *   bool   → "boolean"
 *   date   → "date"
 *   datetime → "datetime"
 *   enum/picklist → "select"
 */
export function fieldSpecToEditorSpec(field: PayloadFieldSpec): ActionFieldSpec {
  const name = field.name;
  const lowered = field.data_type.toLowerCase();

  const hint = /email/i.test(name)
    ? "email"
    : /iban/i.test(name)
      ? "iban"
      : /vat|piva|partita/i.test(name)
        ? "vat_id"
        : null;

  let type: ActionFieldSpec["type"];
  if (hint) {
    type = hint;
  } else if (lowered.includes("currency")) {
    type = "currency";
  } else if (lowered.includes("datetime")) {
    type = "datetime";
  } else if (lowered.includes("date")) {
    type = "date";
  } else if (lowered.includes("bool")) {
    type = "boolean";
  } else if (
    lowered.includes("int") ||
    lowered.includes("float") ||
    lowered.includes("decimal") ||
    lowered.includes("number")
  ) {
    type = "number";
  } else if (
    (field.enum_values?.length ?? 0) > 0 ||
    lowered.includes("enum") ||
    lowered.includes("picklist")
  ) {
    type = "select";
  } else if (lowered.includes("text")) {
    type = "text";
  } else {
    type = "string";
  }

  return {
    name,
    label: name,
    type,
    required: Boolean(field.required),
    readonly: Boolean(field.readonly),
    helperText: field.description || undefined,
    options: (field.enum_values ?? []).map((v) => ({ value: v, label: v })),
    max: field.max_length ?? undefined,
  };
}

// ----- Hook --------------------------------------------------------------

export const payloadSchemaQueryKey = (proposalId: string | null | undefined) =>
  ["payload-schema", proposalId ?? ""] as const;

/**
 * Single slide in a presentation ``document_create`` proposal. Mirrors the
 * BE ``SlideObj`` shipped inside the ``slide_list`` field ``value`` array.
 * Re-exported (alongside the UI ``SlideObj``) so consumers don't reach into
 * ``@ando/ui`` for the shape.
 */
export interface SlideObjWire {
  readonly title: string;
  readonly body: string;
  readonly layout: string;
  readonly image_prompt: string | null;
  readonly source_index?: number;
}

/**
 * Extracted presentation outline — present only when the unified schema
 * carried a ``slide_list`` field (presentation ``document_create``). The
 * ``slides`` come from that field's ``value`` and the allowed layout names
 * from its ``options``; ``title`` is read off the sibling ``title`` text
 * field so the filename stays editable.
 */
export interface PayloadSlideOutline {
  readonly slides: ReadonlyArray<SlideObjWire>;
  readonly layoutOptions: ReadonlyArray<string>;
  readonly title: string;
}

export interface UsePayloadSchemaResult {
  readonly query: UseQueryResult<PayloadSchema>;
  /** Mapped editor specs — pass straight into `<ActionFieldEditor schema={…}>`. */
  readonly editorSchema: readonly ActionFieldSpec[];
  readonly raw: PayloadSchema | undefined;
  /**
   * Current values keyed by field name, lifted off the unified schema's
   * ``field.value``. Seeds the manual "Nuova azione" composer (which starts
   * from an empty or AI-filled proposal) and re-hydrates after an ai-fill.
   * Empty for legacy-endpoint proposals (no per-field value on the wire).
   */
  readonly initialValues: Readonly<Record<string, ActionFieldValue>>;
  /**
   * Presentation outline lifted off the ``slide_list`` field — ``undefined``
   * for any proposal whose schema doesn't carry one (Word / xlsx / pdf
   * document_create, all non-document action types, legacy proposals).
   */
  readonly slideOutline: PayloadSlideOutline | undefined;
}

const ALLOWED_LAYOUT_FALLBACK: ReadonlyArray<string> = [];

/**
 * Coerce an unknown ``slide_list`` field value into a typed ``SlideObjWire``
 * array, dropping malformed entries. Defensive because the value crosses an
 * untyped (``unknown``) wire boundary.
 */
function coerceSlides(value: unknown): ReadonlyArray<SlideObjWire> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw): SlideObjWire[] => {
    if (typeof raw !== "object" || raw === null) return [];
    const r = raw as Record<string, unknown>;
    return [
      {
        title: typeof r.title === "string" ? r.title : "",
        body: typeof r.body === "string" ? r.body : "",
        layout: typeof r.layout === "string" ? r.layout : "",
        image_prompt: typeof r.image_prompt === "string" ? r.image_prompt : null,
        ...(Number.isInteger(r.source_index) && (r.source_index as number) >= 0
          ? { source_index: r.source_index as number }
          : {}),
      },
    ];
  });
}

function extractSlideOutline(
  unified: UnifiedPayloadSchemaWire | undefined,
): PayloadSlideOutline | undefined {
  if (!unified) return undefined;
  const slideField = unified.fields.find((f) => f.type === "slide_list");
  if (!slideField) return undefined;
  const titleField = unified.fields.find((f) => f.name === "title");
  return {
    slides: coerceSlides(slideField.value),
    layoutOptions: slideField.options ?? ALLOWED_LAYOUT_FALLBACK,
    title: typeof titleField?.value === "string" ? titleField.value : "",
  };
}

// The BE ``UnifiedPayloadSchema`` wire shape (``UnifiedFieldSpecWire`` /
// ``UnifiedPayloadSchemaWire``) is the canonical contract — defined ONCE in
// ``@ando/core`` (api/endpoints/actions.ts) and imported above. The manual
// create / ai-fill endpoints return the same shape, so there is a single
// source of truth for both the GET schema and the manual composer.

/**
 * Translate a wire ``UnifiedFieldSpec`` into the legacy
 * ``ActionFieldSpec`` consumed by ``<ActionFieldEditor>``. Mostly a
 * field-by-field rename; the ``is_action_time`` flag propagates so the
 * editor renders action-time params in a distinct section.
 */
/**
 * Translate a BE ``FieldType`` string to the editor ``ActionFieldType``.
 * ``<ActionFieldEditor>`` now renders the full vocabulary including
 * ``file`` (attachment chip list + picker — June 8, 2026). The few BE
 * types without a 1:1 editor type are remapped to the closest editable
 * renderer (``textarea`` → multi-line ``text``). Unknown types degrade
 * to ``select`` (when the BE shipped options) or ``string`` so the
 * field is always editable.
 */
const BE_FIELD_TYPE_REMAP: Readonly<Record<string, ActionFieldSpec["type"]>> = {
  // BE ``FieldType.TEXT`` is single-line by intent (Pydantic side: titles,
  // names, free-text identifiers) but FE ``text`` is the multi-line
  // ``<textarea>`` renderer. The historical mismatch made every BE TEXT
  // field render as a 4-row textarea — visible most clearly on the
  // ``_account_selector_field`` fallback (when zero connections are
  // linked: the empty SELECT degrades to TEXT and the FE rendered a big
  // empty textarea — user feedback June 9, 2026). Remap so:
  //   BE textarea → FE text (multi-line) — already declared below.
  //   BE text     → FE string (single-line) — this entry, symmetric.
  text: "string",
  textarea: "text",
};

const EDITOR_FIELD_TYPES: ReadonlySet<ActionFieldSpec["type"]> = new Set<
  ActionFieldSpec["type"]
>([
  "string",
  "text",
  "richtext",
  "number",
  "boolean",
  "date",
  "datetime",
  "select",
  "multiselect",
  "currency",
  "email",
  "phone",
  "url",
  "iban",
  "vat_id",
  "password",
  "reference",
  "recipients",
  "json",
  "slide_list",
  "file",
]);

/** Coerce a wire ``value`` (``unknown``) to an ``ActionFieldValue``, or
 * ``undefined`` for shapes the scalar editors don't seed (objects, etc.). */
function coerceFieldValue(v: unknown): ActionFieldValue | undefined {
  if (v === null) return null;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return v;
  }
  if (Array.isArray(v) && v.every((x) => typeof x === "string")) {
    return v as string[];
  }
  return undefined;
}

function unifiedSpecToEditorSpec(
  field: UnifiedFieldSpecWire,
): ActionFieldSpec & { readonly isActionTime: boolean } {
  const remapped = BE_FIELD_TYPE_REMAP[field.type] ?? field.type;
  const fallback: ActionFieldSpec["type"] = field.options?.length ? "select" : "string";
  const editorType: ActionFieldSpec["type"] = EDITOR_FIELD_TYPES.has(
    remapped as ActionFieldSpec["type"],
  )
    ? (remapped as ActionFieldSpec["type"])
    : fallback;
  return {
    name: field.name,
    label: field.label || field.name,
    type: editorType,
    // BE-resolved default (e.g. auto-selected single account) — carried so the
    // editor seeds it and doesn't report an auto-filled required field empty.
    value: coerceFieldValue(field.value),
    // Live record "before" value (UPDATE_RECORD) — drives the before→after diff
    // line. ``undefined`` for shapes the scalar editor doesn't diff / absent.
    currentValue: coerceFieldValue(field.current_value),
    required: field.required,
    readonly: field.readonly,
    helperText: field.help_text,
    options: (field.options ?? []).map((v) => ({ value: v, label: field.option_metadata?.[v]?.label ?? v, metadata: field.option_metadata?.[v] })),
    max: field.max_length ?? field.validation?.max_length ?? undefined,
    referenceTo: field.reference_to ?? undefined,
    isActionTime: field.is_action_time,
    peopleSearchConnectionFieldName: field.people_search_connection_field_name ?? undefined,
  };
}

export function usePayloadSchema(proposalId: string | null | undefined): UsePayloadSchemaResult {
  // First-line: the canonical unified endpoint (cdb0999). Falls back to
  // the legacy per-action-type route only on a 404 — at that point we
  // know the proposal predates the unified shape (rare; only the very
  // first wave of action proposals in prod).
  const query = useQuery<PayloadSchema>({
    queryKey: payloadSchemaQueryKey(proposalId),
    queryFn: async () => {
      const pid = encodeURIComponent(proposalId ?? "");
      try {
        // ``apiClient.baseUrl`` already carries the ``/api/v1`` prefix
        // (see ``env.ts::resolveRuntimeEnv``) — passing the full path
        // here would have produced ``/api/v1/api/v1/actions/...`` and
        // a silent 404, leaving the editor schema empty and the HITL
        // card stuck in read-only preview mode (May 25, 2026 user
        // report: "non posso modificare la mail / il mittente").
        const unified = await apiClient.get<UnifiedPayloadSchemaWire>(
          `/actions/${pid}/payload-schema`,
        );
        // Adapt the unified shape to the legacy `PayloadSchema` so existing
        // consumers (action card render, audit, undo) keep working. The
        // editor schema below uses the richer unified mapper directly.
        return {
          proposal_id: unified.action_id,
          provider: unified.provider ?? "",
          sobject_type: unified.sobject_type ?? "",
          for_create: false,
          fields: unified.fields.map((f) => ({
            name: f.name,
            data_type: f.type,
            required: f.required,
            readonly: f.readonly,
            max_length: f.max_length ?? null,
            enum_values: f.options ?? [],
            description: f.help_text ?? "",
            default_value: f.default_value ?? undefined,
            validation_pattern: f.validation?.pattern ?? null,
            reference_to: f.reference_to ?? null,
          })),
          // Stash the original unified payload on a hidden field so the
          // editor can reuse `is_action_time` + `value` without a second
          // network call.
          ...({ __unified: unified } as Record<string, unknown>),
        } as PayloadSchema;
      } catch (err) {
        // Legacy fallback — same shape, same `fieldSpecToEditorSpec` adapter.
        // Same baseUrl rule: no leading ``/api/v1`` — the client adds it.
        const e = err as { status?: number } | null;
        if (e?.status === 404) {
          return apiClient.get<PayloadSchema>(
            `/actions/proposals/${pid}/schema`,
          );
        }
        throw err;
      }
    },
    enabled: Boolean(proposalId),
    staleTime: 60_000,
  });

  // Prefer the unified mapping when present; fall back to the legacy
  // per-vendor mapper for proposals served by the legacy endpoint.
  // MEMOISED on ``query.data`` — react-query keeps a stable data reference, so
  // ``editorSchema`` stays referentially stable across renders. Without this it
  // was a fresh array every render, which (a) re-ran every downstream
  // ``useMemo``/effect keyed on the schema and (b) made the ActionDrawer value
  // seed reset on every render, wiping in-progress edits (the "select doesn't
  // stick" bug on the email ``DA`` picker).
  const unified = (query.data as unknown as { __unified?: UnifiedPayloadSchemaWire } | undefined)?.__unified;
  const editorSchema = useMemo<readonly ActionFieldSpec[]>(
    () =>
      unified
        ? unified.fields.map(unifiedSpecToEditorSpec)
        : (query.data?.fields ?? []).map(fieldSpecToEditorSpec),
    [unified, query.data],
  );

  const initialValues: Record<string, ActionFieldValue> = useMemo(
    () =>
      unified
        ? Object.fromEntries(
            unified.fields.map((f) => [f.name, (f.value ?? null) as ActionFieldValue]),
          )
        : {},
    [unified],
  );

  return {
    query,
    editorSchema,
    raw: query.data,
    initialValues,
    slideOutline: extractSlideOutline(unified),
  };
}
